import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const CreateDOPage = () => {
  const [pharmacies, setPharmacies] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState('');
  const [lineItems, setLineItems] = useState([{ product_id: '', batch_id: '', quantity: 1 }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pharmsRes, prodsRes, batchesRes] = await Promise.all([
        api.get('/pharmacies'),
        api.get('/products'),
        api.get('/batches')
      ]);
      setPharmacies(pharmsRes.data);
      setProducts(prodsRes.data);
      setBatches(batchesRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { product_id: '', batch_id: '', quantity: 1 }]);
  };

  const removeLineItem = (idx) => {
    setLineItems(lineItems.filter((_, i) => i !== idx));
  };

  const updateItem = (idx, field, value) => {
    const items = [...lineItems];
    items[idx][field] = value;
    if (field === 'product_id') {
      // Auto-select FEFO batch
      const productBatches = batches.filter(b => b.product_id === value)
        .sort((a, b) => (a.expiry_year * 100 + a.expiry_month) - (b.expiry_year * 100 + b.expiry_month));
      if (productBatches.length > 0) {
        items[idx].batch_id = productBatches[0].id;
      }
    }
    setLineItems(items);
  };

  const handleSubmit = async () => {
    if (!selectedPharmacy || lineItems.some(li => !li.product_id || !li.batch_id)) {
      toast.error('Please complete all fields');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/delivery-orders', {
        pharmacy_id: selectedPharmacy,
        line_items: lineItems.map(li => ({
          product_id: li.product_id,
          batch_id: li.batch_id,
          quantity: parseInt(li.quantity)
        }))
      });
      toast.success(`DO ${response.data.do_number} created`);
      setSelectedPharmacy('');
      setLineItems([{ product_id: '', batch_id: '', quantity: 1 }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create DO');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>Create Delivery Order</h1>

      <Card className="p-6 space-y-4">
        <div>
          <Label>Pharmacy</Label>
          <Select value={selectedPharmacy} onValueChange={setSelectedPharmacy}>
            <SelectTrigger data-testid="do-pharmacy-select"><SelectValue placeholder="Select pharmacy" /></SelectTrigger>
            <SelectContent>
              {pharmacies.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Line Items</Label>
          <div className="space-y-3 mt-2">
            {lineItems.map((item, idx) => {
              const productBatches = batches.filter(b => b.product_id === item.product_id);
              return (
                <div key={idx} data-testid={`do-line-${idx}`} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <Label className="text-xs">Product</Label>
                    <Select value={item.product_id} onValueChange={(v) => updateItem(idx, 'product_id', v)}>
                      <SelectTrigger><SelectValue placeholder="Product" /></SelectTrigger>
                      <SelectContent>
                        {products.filter(p => p.owner_type === 'distributor').map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Batch (FEFO)</Label>
                    <Select value={item.batch_id} onValueChange={(v) => updateItem(idx, 'batch_id', v)}>
                      <SelectTrigger><SelectValue placeholder="Batch" /></SelectTrigger>
                      <SelectContent>
                        {productBatches.map(b => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.batch_number} - Exp {String(b.expiry_month).padStart(2,'0')}/{b.expiry_year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Quantity</Label>
                    <Input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} />
                  </div>
                  <div className="col-span-1">
                    {lineItems.length > 1 && (
                      <Button size="sm" variant="outline" onClick={() => removeLineItem(idx)}>
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <Button variant="outline" onClick={addLineItem} className="mt-3" size="sm">
            <Plus size={14} className="mr-2" /> Add Line Item
          </Button>
        </div>

        <Button
          data-testid="submit-do-btn"
          onClick={handleSubmit}
          disabled={loading}
          className="w-full"
          style={{ backgroundColor: '#1E3A8A' }}
        >
          {loading ? 'Creating...' : 'Create Delivery Order'}
        </Button>
      </Card>
    </div>
  );
};
